// src/components/SheetPanel.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';

const API_BASE = ''; // relative -> dev proxy (same-origin), matches ProjectStatusPanel

// ── derived-date helpers (Option 1: dates come from task.doneAt) ──
const fmtShortDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric'  }) : '—';

const pctOf = (lvl) =>
  lvl.tasks.length ? Math.round((lvl.tasks.filter((t) => t.done).length / lvl.tasks.length) * 100) : 0;

const levelStart = (lvl) => {
  const ds = lvl.tasks.filter((t) => t.done && t.doneAt).map((t) => t.doneAt);
  return ds.length ? Math.min(...ds) : null;
};
const levelEnd = (lvl) => {
  if (pctOf(lvl) !== 100) return null;
  const ds = lvl.tasks.filter((t) => t.done && t.doneAt).map((t) => t.doneAt);
  return ds.length ? Math.max(...ds) : null;
};

const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

export default function SheetPanel({ project, setSavedProjects }) {
  const [levels, setLevels] = useState(() => project?.projectStatus?.levels || []);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  // reset when the project changes (avoids the stale-useState trap on prop change)
  useEffect(() => {
    setLevels(project?.projectStatus?.levels || []);
  }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    let q = 0, i = 0;
    levels.forEach((l) => l.tasks.forEach((t) => {
      q += Number(t.quotationCost) || 0;
      i += Number(t.invoiceCost) || 0;
    }));
    return { q, i };
  }, [levels]);

  // debounced PUT — mirrors ProjectStatusPanel's save path, updates savedProjects
  const scheduleSave = useCallback((nextLevels) => {
    if (!project?.id) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const updated = {
        ...project,
        projectStatus: { ...(project.projectStatus || {}), levels: nextLevels },
      };
      setSaving(true);
      fetch(`${API_BASE}/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      })
        .then((r) => r.json())
        .then(() => setSavedProjects?.((prev) => prev.map((p) => (p.id === updated.id ? updated : p))))
        .catch(() => {})
        .finally(() => setSaving(false));
    }, 600);
  }, [project, setSavedProjects]);

  const setCost = (li, ti, field, value) => {
    const v = value === '' ? '' : Number(value);
    const next = levels.map((lvl, i) =>
      i !== li ? lvl : { ...lvl, tasks: lvl.tasks.map((t, j) => (j !== ti ? t : { ...t, [field]: v })) });
    setLevels(next);
    scheduleSave(next);
  };

  const exportExcel = () => {
    const rows = [];
    rows.push([project?.name || 'Project']);
    rows.push(['Created', project?.createdAt ? new Date(project.createdAt).toLocaleString() : '—']);
    rows.push([]);
    rows.push(['Task', 'Start', 'Completion', 'Quotation (₹)', 'Invoice (₹)']);
    levels.forEach((lvl) => {
      rows.push([lvl.name, fmtShortDate(levelStart(lvl)), fmtShortDate(levelEnd(lvl))]);
      let sq = 0, si = 0;
      lvl.tasks.forEach((t) => {
        const q = Number(t.quotationCost) || 0;
        const iv = Number(t.invoiceCost) || 0;
        sq += q; si += iv;
        rows.push([`  ${t.label}`, '', t.doneAt ? fmtShortDate(t.doneAt) : '', q || '', iv || '']);
      });
      rows.push([`${lvl.name} — subtotal`, '', '', sq, si]);
      rows.push([]);
    });
    rows.push(['TOTAL BUDGET', '', '', totals.q, totals.i]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 34 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cost Sheet');
    XLSX.writeFile(wb, `VAUOM_CostSheet_${(project?.name || 'project').replace(/\s+/g, '_')}.xlsx`);
  };

  if (!project) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--text-secondary,#c3c9d4)' }}>Select a project.</div>;
  }
  if (!levels.length) {
    return (
      <div style={{ padding: 16, fontSize: 13, color: 'var(--text-secondary,#c3c9d4)' }}>
        No levels yet — build the massing and open the Status tab to generate the task list, then costs can be entered here.
      </div>
    );
  }

  // ── styles ──
  const th = {
    position: 'sticky', top: 0, zIndex: 2, textAlign: 'left',
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px',
    color: 'var(--text-muted,#8b92a0)', padding: '8px 10px',
    background: 'var(--surface-1,#0d1220)', borderBottom: '1px solid var(--border,#2c3444)',
  };
  const thNum = { ...th, textAlign: 'right', width: 130 };
  const thDate = { ...th, width: 92 };
  const td = { padding: '5px 10px', fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', borderBottom: '0.5px solid rgba(255,255,255,0.04)' };
  const tdDate = { ...td, color: 'var(--text-accent,#7fb4f0)', whiteSpace: 'nowrap' };
  const costInput = {
    width: '100%', boxSizing: 'border-box', textAlign: 'right',
    fontSize: 12, padding: '4px 8px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', outline: 'none',
  };
  const subtotalRow = { background: 'var(--surface-2)', fontWeight: 600, color: 'var(--text,#e5e9f0)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid var(--border,#2c3444)', flexShrink: 0 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text,#e5e9f0)' }}>{project.name}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginLeft: 8 }}>
            created {project.createdAt ? new Date(project.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </span>
          {saving && <span style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginLeft: 8 }}>· saving…</span>}
        </div>
        <button type="button" onClick={exportExcel}
          style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
            border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary,#c3c9d4)' }}>
          ⬇ Export Excel
        </button>
      </div>

      {/* scrolling grid */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Task</th>
              <th style={thDate}>Start</th>
              <th style={thDate}>Completion</th>
              <th style={thNum}>Quotation ₹</th>
              <th style={thNum}>Invoice ₹</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((lvl, li) => {
              let sq = 0, si = 0;
              lvl.tasks.forEach((t) => { sq += Number(t.quotationCost) || 0; si += Number(t.invoiceCost) || 0; });
              return (
                <React.Fragment key={lvl.id}>
                  {/* level header row — carries the floor's start / completion */}
                  <tr style={{ background: 'var(--surface-2,#1a2234)' }}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--text,#e5e9f0)' }}>{lvl.name}</td>
                    <td style={tdDate}>{fmtShortDate(levelStart(lvl))}</td>
                    <td style={tdDate}>{fmtShortDate(levelEnd(lvl))}</td>
                    <td style={td} />
                    <td style={td} />
                  </tr>

                  {/* task rows — start blank, completion = doneAt, editable cost cells */}
                  {lvl.tasks.map((t, ti) => (
                    <tr key={ti}>
                      <td style={{ ...td, paddingLeft: 22 }}>{t.label}</td>
                      <td style={td}>—</td>
                      <td style={tdDate}>{t.doneAt ? fmtShortDate(t.doneAt) : '—'}</td>
                      <td style={{ ...td, padding: '3px 8px' }}>
                        <input type="number" min="0" style={costInput}
                          value={t.quotationCost ?? ''} placeholder="—"
                          onChange={(e) => setCost(li, ti, 'quotationCost', e.target.value)} />
                      </td>
                      <td style={{ ...td, padding: '3px 8px' }}>
                        <input type="number" min="0" style={costInput}
                          value={t.invoiceCost ?? ''} placeholder="—"
                          onChange={(e) => setCost(li, ti, 'invoiceCost', e.target.value)} />
                      </td>
                    </tr>
                  ))}

                  {/* level subtotal */}
                  <tr style={subtotalRow}>
                    <td style={{ ...td, ...subtotalRow, paddingLeft: 22 }}>{lvl.name} subtotal</td>
                    <td style={{ ...td, ...subtotalRow }} />
                    <td style={{ ...td, ...subtotalRow }} />
                    <td style={{ ...td, ...subtotalRow, textAlign: 'right' }}>{sq ? inr(sq) : '—'}</td>
                    <td style={{ ...td, ...subtotalRow, textAlign: 'right' }}>{si ? inr(si) : '—'}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>

          {/* grand total — sticky footer */}
          <tfoot>
            <tr>
              <td colSpan={3} style={{ position: 'sticky', bottom: 0, background: 'var(--surface-1,#0d1220)',
                borderTop: '1px solid var(--border,#2c3444)', padding: '10px 10px', fontSize: 13, fontWeight: 700,
                color: 'var(--text,#e5e9f0)' }}>TOTAL BUDGET</td>
              <td style={{ position: 'sticky', bottom: 0, background: 'var(--surface-1,#0d1220)',
                borderTop: '1px solid var(--border,#2c3444)', padding: '10px 10px', fontSize: 13, fontWeight: 700,
                textAlign: 'right', color: 'var(--ok)' }}>{inr(totals.q)}</td>
              <td style={{ position: 'sticky', bottom: 0, background: 'var(--surface-1,#0d1220)',
                borderTop: '1px solid var(--border,#2c3444)', padding: '10px 10px', fontSize: 13, fontWeight: 700,
                textAlign: 'right', color: '#7fb4f0' }}>{inr(totals.i)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
